import { useCallback } from "react";
import { useLingui } from "@lingui/react";
import { useErrorToast } from "../showToast";
import { Errors } from "../../errors";
import {
  useLogin,
  useOTPLogin,
  useOTPSend,
} from "../../controllers/UserController";
import {
  AUTH_INCORRECT_CREDENTIALS_TEXT,
  AUTH_INVALID_EMAIL_TEXT,
  AUTH_LOGIN_CREDENTIALS_FAILED_TEXT,
  AUTH_LOGIN_SERVER_ERROR_TEXT,
  AUTH_SERVER_CONNECTION_FAILED_TEXT,
  AUTH_TOO_MANY_REQUESTS_TEXT,
  UNEXPECTED_ERROR_TEXT,
} from "../constants/i18n-strings";

export function useSignin(handleErrors: boolean) {
  const showErrorToast = useErrorToast();
  const { mutateAsync: login } = useLogin();
  const { mutateAsync: otpLogin } = useOTPLogin();
  const { mutateAsync: otpSend } = useOTPSend();
  const { _: i18n } = useLingui();

  const displayAuthError = useCallback(
    (message: string) => {
      if (message.includes("auth/invalid-email")) {
        showErrorToast(i18n(AUTH_INVALID_EMAIL_TEXT));
      } else if (message.includes("auth/user-not-found")) {
        showErrorToast(i18n(AUTH_INCORRECT_CREDENTIALS_TEXT));
      } else if (message.includes("auth/wrong-password")) {
        showErrorToast(i18n(AUTH_INCORRECT_CREDENTIALS_TEXT));
      } else if (message.includes("too-many-requests")) {
        showErrorToast(i18n(AUTH_TOO_MANY_REQUESTS_TEXT));
      } else if (message.includes(Errors.CSRFLoadFailed)) {
        showErrorToast(i18n(AUTH_SERVER_CONNECTION_FAILED_TEXT));
      } else if (message.includes(Errors.LoginLoadFailed)) {
        showErrorToast(i18n(AUTH_SERVER_CONNECTION_FAILED_TEXT));
      } else if (message.includes(Errors.LoginUnauthorized)) {
        showErrorToast(i18n(AUTH_LOGIN_CREDENTIALS_FAILED_TEXT));
      } else if (message.includes(Errors.InternalServerError)) {
        showErrorToast(i18n(AUTH_LOGIN_SERVER_ERROR_TEXT));
      } else {
        showErrorToast(message);
      }
    },
    [showErrorToast, i18n]
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        await login({ email, password });
        return true;
      } catch (e) {
        if (!handleErrors) throw e;

        if (e instanceof Error) {
          displayAuthError(e.message);
        } else {
          showErrorToast(i18n(UNEXPECTED_ERROR_TEXT));
        }
        return false;
      }
    },
    [showErrorToast, displayAuthError, handleErrors, login, i18n]
  );

  const signInOTP = useCallback(
    async (phonenumber: string, otpCode: string) => {
      try {
        await otpLogin({ phonenumber, otpCode });
        return true;
      } catch (e) {
        if (!handleErrors) throw e;

        if (e instanceof Error) {
          displayAuthError(e.message);
        } else {
          showErrorToast(i18n(UNEXPECTED_ERROR_TEXT));
        }
        return false;
      }
    },
    [showErrorToast, displayAuthError, handleErrors, otpLogin, i18n]
  );

  const sendOTP = useCallback(
    async (phonenumber: string) => {
      try {
        await otpSend(phonenumber);
        return true;
      } catch (e) {
        if (!handleErrors) throw e;

        if (e instanceof Error) {
          displayAuthError(e.message);
        } else {
          showErrorToast(i18n(UNEXPECTED_ERROR_TEXT));
        }
        return false;
      }
    },
    [showErrorToast, displayAuthError, handleErrors, otpSend, i18n]
  );

  return { signIn, signInOTP, sendOTP, displayAuthError };
}
